<template>
    <user-team-card-legacy
        :user="order?.assignedTo"
        :team="order?.assignedToBranch"
        :assignable-users="legacyAssignableResponsibleUsers"
        :editable="isUserOfAssignedCompany"
        @user-change="changeAssignee($event)"
    >
        <template #title>
            <text-decorator :size="FontSize.sm">
                Verantwoordelijk
            </text-decorator>
        </template>
    </user-team-card-legacy>
</template>

<script setup lang="ts">
    import { FontSize, TextDecorator } from '@keyvue/one-ui';
    import { Lead, Team, User } from '@keyvue/one-api-client';
    import { computed } from 'vue';
    import { Order, OrderAPI, User as LegacyUser } from '@keyvue/one-api-client-legacy';
    import useClient from '@composables/useClient';
    import useLeadSecurity from '@composables/security/useLeadSecurity';
    import useNotify from '@composables/useNotify';
    import UserTeamCardLegacy from '@components/Cards/UserTeamCardLegacy.vue';
    import useEventBus from '@composables/useEventBus';

    export interface Props {
        order: Order
        lead: Lead
    }

    const props = defineProps<Props>();
    const { client } = useClient();
    const { notifySaving, notifySaved } = useNotify();
    const { isUserOfAssignedCompany } = await useLeadSecurity(props.lead);
    const assignedToTeam = await client.get(Team, props.order.assignedToBranch?.id ?? '');
    const eventBus = useEventBus();

    const [
        assignableResponsibleUserCollection,
    ] = await Promise.all([
        await client.getSubResources(assignedToTeam, User, 'available-users'),
    ]);

    const assignableResponsibleUsers = computed(() => assignableResponsibleUserCollection.data);

    const legacyAssignableResponsibleUsers = computed(() => assignableResponsibleUsers.value.map((user: User) => LegacyUser.fromData({
        id: user.id.toString(),
        firstname: user.name?.firstName,
        middlename: user.name?.middleName,
        lastname: user.name?.lastName,
    })));

    const changeOrderField = async (callback: (o: Order) => Order) => {
        const closeNotification = notifySaving(undefined, 0);
        const legacyOrder = await OrderAPI.get(props.order.id);
        await OrderAPI.update(props.order.id, callback(legacyOrder));
        notifySaved();
        if (closeNotification) {
            closeNotification();
        }
    };

    const changeAssignee = async (user: LegacyUser) => {
        const legacyUser = LegacyUser.fromOne(user);
        await changeOrderField((o: Order) => {
            const changedOrder = o;
            changedOrder.assignedTo = legacyUser;
            return changedOrder;
        });

        eventBus.dispatch('resource-manipulated', 'Order');
    };
</script>
